import { ApplicationRef, ComponentRef, Inject, Injectable, NgModuleRef, ViewContainerRef } from '@angular/core';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { MobileContentNavigationComponent } from '@finder/shared/components/mobile-content-navigation/mobile-content-navigation.component';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class ContentNavigationService {

    activeLink: any;
    viewContainerRef: ViewContainerRef;
    mobileComponentRef: ComponentRef<MobileContentNavigationComponent>;

    private linkChanged$ = new BehaviorSubject<string>(void 0);

    constructor(
        private windowRef: WindowRef,
        @Inject(ApplicationRef) private app,
        private localeService: LocaleService,
    ) { }

    init(links: any[], logo: any, cta: any, ariaLabel?: string) {
        this.initActiveLink(links);
        this.viewContainerRef = this.app.components[0].instance.rootComponents;
        this.createMobileComponent(links, logo, cta, ariaLabel);
    }

    observeActiveLinkChange(): Observable<string> {
        return this.linkChanged$.asObservable();
    }

    initActiveLink(links: any[]): void {
        const locale = `/${this.localeService.getLocale()}`;
        const { pathname, hash } = this.windowRef.nativeWindow.location;
        const pathnameWithoutLocale = pathname.startsWith(locale) ?
            pathname.slice(locale.length) : pathname;

        // Check to see if any of the content-navigation links match the current URL.
        this.activeLink = links.reduce((found, link) => {
            // If we already found a match with a pageAnchor, let's just use that.
            if (found?.pageAnchor) {
                return found;
            }

            // If the link has a pageAnchor, we need to match both pathname and hash.
            // If it doesn't have a pageAnchor, we just match the pathname.
            // If we don't find a match, just return what we had before (which may be undefined).
            return ((link.pageAnchor && hash) ?
                `${pathnameWithoutLocale}${hash}` === link.href :
                pathnameWithoutLocale === link.href) ?
                link : found;
        }, void 0);

        this.linkChanged$.next(this.activeLink);
    }

    disconnect() {
        this.removeMobileComponent();
    }

    update(links, logo, cta, ariaLabel?: string) {
        this.mobileComponentRef.instance.links = links;
        this.mobileComponentRef.instance.logo = logo;
        this.mobileComponentRef.instance.cta = cta;
        this.mobileComponentRef.instance.activeLink = this.activeLink || /* istanbul ignore else */ links[0];
        this.mobileComponentRef.instance.ariaLabel = ariaLabel;
    }

    setActiveLink(link, disableMobile, isResponsiveNavigation) {
        this.activeLink = link;

        if (disableMobile !== true && !isResponsiveNavigation) {
            this.mobileComponentRef.instance.activeLink = this.activeLink;
        }

        this.linkChanged$.next(this.activeLink);
    }

    private createMobileComponent(links, logo, cta, ariaLabel?: string) {
        // Create mobile component and update
        this.mobileComponentRef = this.viewContainerRef.createComponent(MobileContentNavigationComponent);
        this.update(links, logo, cta, ariaLabel);
    }

    private removeMobileComponent() {
        if (this.mobileComponentRef) {
            this.mobileComponentRef.destroy();
        }
    }
}
